import { AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import BooleanSelect from "@/components/onboarding/shared/BooleanSelect";
import type { DocumenteAnexateData } from "@/types/entity";

const EMPTY_DOCUMENTE: DocumenteAnexateData = {
  avem_documente: null,
  nr_documente: "",
  doc_dimensiune: "",
  art9docs: "",
  alte_documente: "",
};

type DocumenteAnexateSectionProps = {
  data: DocumenteAnexateData | null;
  onChange: (data: DocumenteAnexateData) => void;
  fieldErrors: Record<string, string>;
};

function FieldError({ error }: { error: string | undefined }) {
  if (!error) return null;
  return (
    <div className="flex items-center gap-2 mt-1">
      <AlertTriangle className="h-3.5 w-3.5 text-destructive shrink-0" />
      <span className="text-xs text-destructive">{error}</span>
    </div>
  );
}

function DocumenteAnexateSection({
  data,
  onChange,
  fieldErrors,
}: DocumenteAnexateSectionProps) {
  const current = data ?? EMPTY_DOCUMENTE;
  const showDetails = current.avem_documente === true;

  const updateField = (
    field: keyof DocumenteAnexateData,
    value: string | boolean | null
  ) => {
    onChange({ ...current, [field]: value });
  };

  return (
    <div className="py-2 space-y-4">
      <div className="border border-ds-border-200 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between gap-4 px-4 py-3">
          <span className="text-xs md:text-sm text-ds-text-600 flex-1">
            Avem documente anexate? <span className="text-destructive">*</span>
          </span>
          <div className="w-32 shrink-0">
            <BooleanSelect
              value={current.avem_documente}
              onChange={(val) => updateField("avem_documente", val)}
              variant="neutral"
            />
          </div>
        </div>
        <FieldError error={fieldErrors["documente_anexate.avem_documente"]} />
      </div>

      {showDetails && (
        <div className="border border-ds-border-200 rounded-lg p-4 space-y-4">
          <div>
            <Label className="text-xs md:text-sm font-medium">
              Număr documente anexate{" "}
              <span className="text-destructive">*</span>
            </Label>
            <Input
              type="text"
              inputMode="numeric"
              placeholder="ex: 3"
              value={current.nr_documente}
              onChange={(e) => updateField("nr_documente", e.target.value)}
              className="mt-1"
            />
            <FieldError error={fieldErrors["documente_anexate.nr_documente"]} />
          </div>

          <div>
            <Label className="text-xs md:text-sm font-medium">
              Documente anexate care atestă încadrarea întreprinderii conform
              criteriului dimensiune <span className="text-destructive">*</span>
            </Label>
            <Input
              placeholder="ex: Bilanț, Certificat ONRC"
              value={current.doc_dimensiune}
              onChange={(e) => updateField("doc_dimensiune", e.target.value)}
              className="mt-1"
            />
            <FieldError
              error={fieldErrors["documente_anexate.doc_dimensiune"]}
            />
          </div>

          <div>
            <Label className="text-xs md:text-sm font-medium">
              Autoevaluarea aplicării art. 9 din OUG nr. 155/2024, după caz
            </Label>
            <Input
              placeholder="ex: Extras Regis"
              value={current.art9docs}
              onChange={(e) => updateField("art9docs", e.target.value)}
              className="mt-1"
            />
          </div>

          <div>
            <Label className="text-xs md:text-sm font-medium">
              Alte documente anexate, solicitate de către DNSC
            </Label>
            <Input
              placeholder="ex: N/A"
              value={current.alte_documente}
              onChange={(e) => updateField("alte_documente", e.target.value)}
              className="mt-1"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default DocumenteAnexateSection;
